import {
  Component,
  OnInit,
  Input,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnChanges,
} from "@angular/core";
import { Router } from "@angular/router";
import {
  LoanDocumentService,
  LoanFileTemplateData,
  GetFileData,
  CompanyColorSchemaEnum,
  BrandingService,
  CompanyBranding,
  NeedsAttentionModel,
  CustomerLoanStatusLabel,
  SsoAccountData,
  SsoAccountCheckoutState,
  IdentityVerificationAction,
  LoanHelper,
  CustomerLoanStatus,
  AgreementSigner,
  AgreementSigningStatus,
  NeedsAttentionHelper,
} from "common";
import {
  LoanData,
  LoanFileTemplate,
  ProductCode
} from "../../loan/domain/models/loan.model";
import { LoanFileTemplateDialogComponent } from "../../loan/loan-file-template-dialog/loan-file-template-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { LoanFileTemplateDialogData } from "../../loan/loan-file-template-dialog/models/loan-file-template-dialog-data.model";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

@UntilDestroy()
@Component({
    selector: "ifp-dashboard-account-widget",
    templateUrl: "./account-widget.component.html",
    styleUrls: ["./account-widget.component.scss"],
    standalone: false
})
export class DashboardAccountWidgetComponent
  implements OnInit, AfterViewInit, OnChanges
{
  disableMatTooltip: boolean = true;

  @ViewChild("loanDescription", { static: false }) loanDescription: ElementRef;
  @Input() currentLoan: LoanData;
  @Input() loansTemplates: LoanFileTemplate[];
  @Input() currentSsoAccount: SsoAccountData;
  @Input() checkoutState: SsoAccountCheckoutState;
  @Input() needsAttentionModel: NeedsAttentionModel;

  /** Every owner who must sign; empty unless the agreement is outstanding. */
  agreementSigners: AgreementSigner[] = [];

  currentLoanTemplates: LoanFileTemplateData[];
  drawEnabled: boolean;
  payEnabled: boolean;
  productCodes = ProductCode;
  loanStatus = CustomerLoanStatus;
  isIdentityVerified: boolean;
  legalEmail: string;
  loading = true;
  barColor: string = "#1c9b41";
  shouldDisplayLoanBarChart: boolean;
  hasCurrentLoanPending: boolean;
  hasAvailableRenewalToSign: boolean;
  isRenewalEligibleToAccept: boolean;
  balanceExceedsTheAmountOfTheOffer: boolean;
  showSignButton: boolean;
  showWaitingForOthers: boolean;
  isOrphanted: boolean;
  maskedLoanNumber: string;
  status: string;
  balance: number;

  constructor(
    private router: Router,
    private loanDocumentService: LoanDocumentService,
    private dialog: MatDialog,
    private brandingService: BrandingService
  ) {}

   ngAfterViewInit(): void {
    this.disableMatTooltip =
      this.loanDescription?.nativeElement?.offsetWidth >=
      this.loanDescription?.nativeElement?.scrollWidth;
  }

  ngOnInit(): void {
    this.getBrandingInfo();
  }

  ngOnChanges(): void {
    this.initCurrentLoanTemplates();
    this.getData();
  }

  verifyIdentity(): void {
    this.router.navigate(["/account/setup/verify-identity"]);
  }

  signAgreement(): void {
    this.router.navigate([`/account/${this.currentLoan.id}/setup/sign-agreement`]);
  }

  details(): void {
    if (this.currentLoan?.isNPLLoan) {
      this.router.navigate([`/account/${this.currentLoan.id}/bank-account`]);
    } else {
      this.router.navigate([`/account/${this.currentLoan.id}/transactions`]);
    }
  }

  draw(): void {
    if(this.needsAttentionModel?.isApplicable) {
      NeedsAttentionHelper.openNeedsAttentionDialog(this.dialog, this.needsAttentionModel.messageInput, this.currentLoan.productCode);
      return;
    }

    this.router.navigate([`/account/${this.currentLoan?.id}/transfer/draw`]);
  }

  pay(): void {
    this.router.navigate([`/account/${this.currentLoan?.id}/transfer/payment`]);
  }

  onClickLoanTemplateOption(loanTemplate: LoanFileTemplateData): void {
      this.loanDocumentService
      .generateFileFromTemplate(this.currentLoan?.id, loanTemplate.id, true)
      .subscribe((fileData: GetFileData) => {
        const data: LoanFileTemplateDialogData = {
          loanDescription: this.currentLoan?.description,
          loanNumber: this.currentLoan?.loanNumber,
          loanTemplate,
          fileData,
        };
        LoanFileTemplateDialogComponent.show(this.dialog, data).subscribe();
      });
  }

  private getBrandingInfo(): void {
    this.brandingService.getBranding$()
    .pipe(untilDestroyed(this))
    .subscribe((branding: CompanyBranding) => {
      this.legalEmail = branding?.legalEmail ?? "legal@ideafinancial.com";
      if (branding.colorSchema === CompanyColorSchemaEnum.LevelEsq) {
        this.barColor = "#974142";
      }
    });
  }

  private initCurrentLoanTemplates(): void {
    this.currentLoanTemplates = this.loansTemplates?.find(
      (template) => template.loanId === this.currentLoan?.id
    )?.templates;
  }

  private getData(): void {
    this.loading = true;
    this.drawEnabled = this.currentLoan?.isDrawVisible;
    this.payEnabled = this.currentLoan?.isPayVisible;
    this.isIdentityVerified = this.checkoutState?.identityVerificationAction === IdentityVerificationAction.Skip;

    const currentLoanAgreementState = this.currentSsoAccount?.loans?.find(
      l => l.loanId === this.currentLoan?.id
    );

    const state = currentLoanAgreementState?.agreementState;
    this.hasCurrentLoanPending = state?.agreementSigningStatus === AgreementSigningStatus.PendingCurrentUser;
    this.hasAvailableRenewalToSign = currentLoanAgreementState?.renewalApplication?.agreementSigningStatus === AgreementSigningStatus.PendingCurrentUser;
    this.isRenewalEligibleToAccept = this.hasAvailableRenewalToSign;
    this.balanceExceedsTheAmountOfTheOffer = state?.errors?.balanceExceedsOffer ?? false;
    this.showSignButton = this.hasCurrentLoanPending;
    this.showWaitingForOthers = state?.agreementSigningStatus === AgreementSigningStatus.PendingOthers;
    this.agreementSigners = this.hasAvailableRenewalToSign
      ? currentLoanAgreementState?.renewalApplication?.signers ?? []
      : this.hasCurrentLoanPending || this.showWaitingForOthers
        ? state?.signers ?? []
        : [];
    this.isOrphanted = !state?.isLoanVisibleForUser;
    this.maskedLoanNumber = this.getMaskedLoanNumber();
    this.status = CustomerLoanStatusLabel.get(this.currentLoan?.customerFacingStatus);
    this.balance = LoanHelper.getOutstandingBalance(this.currentLoan);

    this.shouldDisplayLoanBarChart = !!(this.currentLoan?.loanInfo?.principalBalance &&
      this.currentLoan?.loanInfo?.paymentAmount &&
      !this.currentLoan?.noAutoPayments);

    this.loading = false;
  }

  private getMaskedLoanNumber(): string {
    return this.currentLoan?.loanNumber?.length > 4
      ? "*" +
          this.currentLoan.loanNumber.substr(
            this.currentLoan.loanNumber.length - 4
          )
      : null;
  }
}
